import React from 'react';
import { Trans } from '@lingui/react/macro';
import { PiBuildingsDuotone, PiCalendarDuotone, PiCurrencyDollarDuotone, PiFileXlsDuotone, PiHashDuotone, PiPercentDuotone, PiTagDuotone } from 'react-icons/pi';
import { IconType } from 'react-icons';
import useBuilderLoaderCycle, { BuilderLoaderPhase } from 'src/hooks/useBuilderLoaderCycle';
import './InventoryScheduleLoadingSkeleton.css';

type CellKind = 'id' | 'text' | 'date' | 'tag' | 'percent' | 'amount';

// The template's columns, left to right; the icon stands in for the header label.
const COLUMNS: { Icon: IconType; kind: CellKind; width: number }[] = [
  { Icon: PiHashDuotone, kind: 'id', width: 34 },
  { Icon: PiBuildingsDuotone, kind: 'text', width: 62 },
  { Icon: PiCalendarDuotone, kind: 'date', width: 44 },
  { Icon: PiTagDuotone, kind: 'tag', width: 56 },
  { Icon: PiPercentDuotone, kind: 'percent', width: 46 },
  { Icon: PiCurrencyDollarDuotone, kind: 'amount', width: 60 },
];

const GUTTER_X = 12;
const GRID_X = 26;
const GRID_RIGHT = 328;
const HEADER_Y = 28;
const HEADER_HEIGHT = 16;
const BODY_Y = HEADER_Y + HEADER_HEIGHT;
const ROW_HEIGHT = 14;
const ROW_COUNT = 9;
const BODY_BOTTOM = BODY_Y + ROW_COUNT * ROW_HEIGHT;

const columnX = COLUMNS.map((_, idx) => GRID_X + COLUMNS.slice(0, idx).reduce((sum, c) => sum + c.width, 0));

// Fixed "random" fill lengths (0–1) per row, so every cycle types the same believable data.
const FILL = [0.72, 0.45, 0.9, 0.58, 0.8, 0.38, 0.66, 0.52, 0.84];
const fillAt = (row: number, col: number) => FILL[(row * 3 + col * 5) % FILL.length];

// Columns land on the same beat as the other builder skeletons...
const FIRST_COLUMN_AT = 400;
const COLUMN_STAGGER = 230;
// ...then rows stream in at a constant rate behind a scanning active-row highlight.
const ROWS_START_AT = FIRST_COLUMN_AT + COLUMNS.length * COLUMN_STAGGER + 350;
const ROW_INTERVAL = 170;
const CYCLE_TIMINGS = { runAt: ROWS_START_AT + ROW_COUNT * ROW_INTERVAL + 400, exitAt: 5500, restartAt: 6000 };

const ms = (name: string, value: number) => ({ [name]: `${value}ms` } as React.CSSProperties);

function Cell({ kind, x, y, width, fill }: { kind: CellKind; x: number; y: number; width: number; fill: number }) {
  const cy = y + ROW_HEIGHT / 2;
  const inner = width - 10;
  switch (kind) {
    case 'date':
      return (
        <>
          {[8, 8, 14].map((w, i) => (
            <rect key={i} x={x + 5 + [0, 10, 20][i]} y={cy - 1.25} width={w} height="2.5" rx="1.25" className="iss-value" />
          ))}
        </>
      );
    case 'tag': {
      const tone = ['iss-tag--blue', 'iss-tag--amber', 'iss-tag--violet'][Math.round(fill * 100) % 3];
      return <rect x={x + 5} y={cy - 3} width={16 + fill * 22} height="6" rx="3" className={`iss-tag ${tone}`} />;
    }
    case 'percent':
    case 'amount': {
      // Numbers sit right-aligned, as they do in the spreadsheet.
      const w = inner * (kind === 'percent' ? 0.3 + fill * 0.3 : 0.4 + fill * 0.5);
      return <rect x={x + width - 5 - w} y={cy - 1.25} width={w} height="2.5" rx="1.25" className="iss-value" />;
    }
    case 'id':
      return <rect x={x + 5} y={cy - 1.25} width={8 + fill * 8} height="2.5" rx="1.25" className="iss-value iss-value--muted" />;
    default:
      return <rect x={x + 5} y={cy - 1.25} width={inner * (0.45 + fill * 0.5)} height="2.5" rx="1.25" className="iss-value" />;
  }
}

/** The empty sheet: frame, file strip, row-number gutter and ruled cells, present from the first frame. */
function EmptySheet() {
  return (
    <>
      <rect x="8.5" y="8.5" width="323" height="167" rx="6" className="iss-sheet" />
      <rect x="14" y="12" width="12" height="12" rx="2.5" className="iss-file-badge" />
      <PiFileXlsDuotone x={16} y={14} size={8} className="iss-file-icon" />
      <rect x="31" y="16.75" width="46" height="2.5" rx="1.25" className="iss-value iss-value--muted" />

      <rect x={GUTTER_X} y={HEADER_Y} width={GRID_RIGHT - GUTTER_X} height={HEADER_HEIGHT} className="iss-header-band" />
      <rect x={GUTTER_X} y={BODY_Y} width={GRID_X - GUTTER_X} height={BODY_BOTTOM - BODY_Y} className="iss-header-band" />
      {Array.from({ length: ROW_COUNT }, (_, row) => (
        <rect
          key={row}
          x={GUTTER_X + 4}
          y={BODY_Y + row * ROW_HEIGHT + 5.75}
          width="6"
          height="2.5"
          rx="1.25"
          className="iss-value iss-value--muted"
        />
      ))}

      {Array.from({ length: ROW_COUNT + 1 }, (_, row) => (
        <path key={row} d={`M ${GUTTER_X} ${BODY_Y + row * ROW_HEIGHT} H ${GRID_RIGHT}`} className="iss-rule" />
      ))}
      {[GUTTER_X, ...columnX, GRID_RIGHT].map(x => (
        <path key={x} d={`M ${x} ${HEADER_Y} V ${BODY_BOTTOM}`} className="iss-rule" />
      ))}
      <path d={`M ${GUTTER_X} ${HEADER_Y} H ${GRID_RIGHT}`} className="iss-rule" />
    </>
  );
}

/** A column being inserted: its body flashes while the header (icon + label bar) drops into place. */
function ColumnHeader({ idx }: { idx: number }) {
  const { Icon, width } = COLUMNS[idx];
  const x = columnX[idx];
  const cy = HEADER_Y + HEADER_HEIGHT / 2;
  const style = ms('--drop-delay', FIRST_COLUMN_AT + idx * COLUMN_STAGGER);
  return (
    <>
      <rect x={x} y={HEADER_Y} width={width} height={BODY_BOTTOM - HEADER_Y} className="iss-column-flash" style={style} />
      <g className="iss-drop" style={style}>
        <rect x={x + 0.5} y={HEADER_Y + 0.5} width={width - 1} height={HEADER_HEIGHT - 1} className="iss-header-cell" />
        <rect x={x + 4} y={cy - 4.5} width="9" height="9" rx="2" className="iss-chip" />
        <Icon x={x + 5.5} y={cy - 3} size={6} className="iss-accent" />
        <rect x={x + 16} y={cy - 1.25} width={Math.min(width - 22, 26)} height="2.5" rx="1.25" className="iss-label" />
      </g>
    </>
  );
}

function DataRow({ row }: { row: number }) {
  const y = BODY_Y + row * ROW_HEIGHT;
  return (
    <g className="iss-row" style={ms('--drop-delay', ROWS_START_AT + row * ROW_INTERVAL)}>
      {COLUMNS.map(({ kind, width }, col) => (
        <Cell key={col} kind={kind} x={columnX[col]} y={y} width={width} fill={fillAt(row, col)} />
      ))}
    </g>
  );
}

/**
 * An inventory schedule template being filled in, the way institutions actually work with it —
 * as a spreadsheet: the empty sheet, then its columns added one by one, then rows streaming in
 * steadily. No words, so it reads the same in every language.
 */
function InventorySheetDiagram({ phase }: { phase: BuilderLoaderPhase }) {
  return (
    <svg viewBox="0 0 340 184" className={`inventory-skeleton-illustration is-${phase}`} aria-hidden="true">
      <EmptySheet />

      <g className="iss-stage">
        {COLUMNS.map((_, idx) => (
          <ColumnHeader key={idx} idx={idx} />
        ))}
        {Array.from({ length: ROW_COUNT }, (_, row) => (
          <DataRow key={row} row={row} />
        ))}

        {/* Active-row highlight that scans down at the same constant pace the rows fill in. */}
        <rect
          x={GUTTER_X}
          y={BODY_Y}
          width={GRID_RIGHT - GUTTER_X}
          height={ROW_HEIGHT}
          className="iss-cursor"
          style={{
            ...ms('--drop-delay', ROWS_START_AT),
            ...ms('--scan-duration', ROW_COUNT * ROW_INTERVAL),
            ['--scan-distance' as string]: `${ROW_COUNT * ROW_HEIGHT}px`,
          }}
        />
        {/* Ready: a select-all range grows from the first cell over everything that was filled. */}
        <rect x={GRID_X} y={BODY_Y} width={GRID_RIGHT - GRID_X} height={BODY_BOTTOM - BODY_Y} className="iss-selection" />
      </g>
    </svg>
  );
}

function InventoryScheduleLoadingSkeleton() {
  const { cycle, phase } = useBuilderLoaderCycle(CYCLE_TIMINGS);

  return (
    <div className="inventory-loading-skeleton flex-grow">
      <InventorySheetDiagram key={cycle} phase={phase} />
      <div>
        <p className="inventory-loading-title">
          <Trans>Setting up your schedule builder...</Trans>
        </p>
        <p className="inventory-loading-subtitle">
          <Trans>Preparing your inventory columns</Trans>
        </p>
      </div>
    </div>
  );
}

export default InventoryScheduleLoadingSkeleton;
